import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ALLOWED_EXTENSIONS, validateFile } from '@/utils/fileValidation';
import { Capacitor } from '@capacitor/core';
import { Trans } from 'react-i18next';
import { mobileWidth } from '@/constants';

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onFilesSelect: (files: File[]) => void;
  multiple?: boolean;
}

const DropZone = styled('div')(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  cursor: 'pointer',
  backgroundColor: theme.palette.action.hover,
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  marginBottom: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
}));

const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  onFilesSelect,
  multiple = false,
}) => {
  const theme = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<
    string | React.ReactNode | null
  >(null);
  const isNative = Capacitor.getPlatform() !== 'web';
  const isMobile = useMediaQuery(mobileWidth);
  // Phones pick files with the system picker only; dragging is a desktop gesture.
  const acceptsDrop = !isNative && !isMobile;

  useEffect(() => {
    if (open && isNative) {
      requestAnimationFrame(() => {
        fileInputRef.current?.click();
      });
    }
  }, [open, isNative]);

  const processFiles = useCallback(
    (fileList: FileList) => {
      const files = Array.from(fileList).slice(0, multiple ? undefined : 1);
      for (const file of files) {
        const errorMsg = validateFile(file);
        if (errorMsg) {
          setUploadError(errorMsg);
          return;
        }
      }

      setUploadError(null);
      onFilesSelect(files);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onClose();
    },
    [multiple, onFilesSelect, onClose],
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      processFiles(event.target.files);
    }
  };

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      if (event.dataTransfer.files.length > 0) {
        processFiles(event.dataTransfer.files);
        event.dataTransfer.clearData();
      }
    },
    [processFiles],
  );

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(true);
    },
    [],
  );

  // On phone widths the popup takes no drops, but the browser would still
  // open a dropped file and leave the app.
  const ignoreDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };
  const blocksDrop = !isNative && isMobile;

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
    },
    [],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='upload-modal-title'
      fullScreen={isMobile}
      slotProps={{ paper: { sx: isMobile ? undefined : { width: 400 } } }}
      onDragOver={blocksDrop ? ignoreDrop : undefined}
      onDrop={blocksDrop ? ignoreDrop : undefined}
    >
      <ModalContent>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          id='upload-modal-title'
          variant='h6'
          component='h2'
          mb={2}
        >
          Upload Document
        </Typography>

        <input
          type='file'
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          multiple={multiple}
          accept={ALLOWED_EXTENSIONS.map((ext) => '.' + ext).join(',')}
        />

        <DropZone
          onClick={handleDropZoneClick}
          onDragOver={acceptsDrop ? handleDragOver : undefined}
          onDragLeave={acceptsDrop ? handleDragLeave : undefined}
          onDrop={acceptsDrop ? handleDrop : undefined}
          sx={{
            borderColor: isDragging
              ? theme.palette.primary.main
              : theme.palette.divider,
            backgroundColor: isDragging
              ? theme.palette.action.selected
              : theme.palette.action.hover,
          }}
        >
          <UploadFileIcon
            sx={{ fontSize: 40, color: theme.palette.text.secondary, mb: 1 }}
          />
          {acceptsDrop && (
            <>
              <Typography
                variant='body1'
                color='textSecondary'
              >
                Drag and drop file here
              </Typography>
              <Typography
                variant='body2'
                color='textSecondary'
                mb={1}
              >
                or
              </Typography>
            </>
          )}
          <Button
            variant='outlined'
            size='large'
          >
            {isNative ? 'Choose File' : 'Select File'}
          </Button>
          <Typography
            variant='caption'
            color='textSecondary'
            sx={{ fontSize: '12px', textAlign: 'center', lineHeight: '1.4' }}
          >
            <Trans
              i18nKey='uploadModal.allowedFiles'
              components={{ bold: <strong /> }}
            >
              You can upload images, documents, and data files, including{' '}
              <strong>JPG</strong>, <strong>PNG</strong>, <strong>PDF</strong>,{' '}
              <strong>Word</strong>, <strong>Excel</strong>,{' '}
              <strong>PowerPoint</strong>, <strong>Pages</strong>,{' '}
              <strong>CSV</strong>, <strong>TXT</strong>, <strong>HEIC</strong>,{' '}
              <strong>WEBP</strong>, and <strong>QFX</strong>. Max file size is{' '}
              <strong>50 MB</strong>.
            </Trans>
          </Typography>
        </DropZone>
        {uploadError && (
          <Typography
            color='error'
            variant='body2'
            mb={2}
          >
            {uploadError}
          </Typography>
        )}
        <Button
          size='large'
          variant='outlined'
          onClick={onClose}
          fullWidth
        >
          Cancel
        </Button>
      </ModalContent>
    </Dialog>
  );
};

export default UploadModal;
